import { useEffect, useRef, useState } from 'react';
import { useAnimatedModal } from '../../../../../utils/hooks/useAnimatedModal';
import * as S from './styles';

const CLOSE_DRAG_THRESHOLD_PX = 100;
const DRAG_START_TOLERANCE_PX = 6;
const SHEET_ANIMATION_DURATION_MS = 300;

export interface HelperItem {
    title: string;
    raw_text: string;
}

interface HelperProps {
    label?: string;
    items: HelperItem[];
    color: string;
}

export const Helper = ({ label, items, color }: HelperProps) => {
    const sheet = useAnimatedModal();
    const [openQuestionIndex, setOpenQuestionIndex] = useState<number | null>(null);
    const [dragOffset, setDragOffset] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const touchStartY = useRef<number | null>(null);
    const sheetRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!sheet.show) return;

        const closeOnBackNavigation = () => sheet.close();
        window.addEventListener('popstate', closeOnBackNavigation);
        return () => window.removeEventListener('popstate', closeOnBackNavigation);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sheet.show]);

    if (!(items || []).length) return <></>;

    const sheetTitle = label ?? 'Saiba mais';

    const openSheet = () => {
        window.history.pushState({ helperOpen: true }, '');
        setOpenQuestionIndex(null);
        setDragOffset(null);
        setIsDragging(false);
        sheet.open();
    };

    const closeSheet = () => window.history.back();

    const isContentScrolled = () => (sheetRef.current?.scrollTop ?? 0) > 0;

    const handleTouchStart = (event: React.TouchEvent) => {
        if (isContentScrolled()) return;
        touchStartY.current = event.touches[0].clientY;
    };

    const handleTouchMove = (event: React.TouchEvent) => {
        if (touchStartY.current === null) return;
        const draggedDistance = event.touches[0].clientY - touchStartY.current;

        const gestureIsScroll = draggedDistance < 0 || isContentScrolled();
        if (!isDragging && gestureIsScroll) {
            touchStartY.current = null;
            return;
        }

        if (draggedDistance <= DRAG_START_TOLERANCE_PX) {
            if (isDragging) setDragOffset(0);
            return;
        }

        if (!isDragging) setIsDragging(true);
        setDragOffset(draggedDistance - DRAG_START_TOLERANCE_PX);
    };

    const handleTouchEnd = () => {
        if (touchStartY.current === null) return;
        touchStartY.current = null;

        if (!isDragging) return;
        setIsDragging(false);

        if ((dragOffset ?? 0) > CLOSE_DRAG_THRESHOLD_PX) {
            const sheetHeight = sheetRef.current?.offsetHeight ?? window.innerHeight;
            setDragOffset(sheetHeight);
            setTimeout(closeSheet, SHEET_ANIMATION_DURATION_MS);
            return;
        }

        setDragOffset(0);
        setTimeout(() => setDragOffset(null), SHEET_ANIMATION_DURATION_MS);
    };

    return (
        <>
            <S.Toggle type="button" $color={color} onClick={openSheet}>
                {sheetTitle}
            </S.Toggle>

            {sheet.show && (
                <S.ModalOverlay $closing={sheet.isClosing} onClick={closeSheet}>
                    <S.ModalSheet
                        ref={sheetRef}
                        $closing={sheet.isClosing}
                        $drag={dragOffset}
                        $isDragging={isDragging}
                        onClick={(event) => event.stopPropagation()}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchEnd}
                    >
                        <S.SheetHandle />
                        <S.ModalTitle>{sheetTitle}</S.ModalTitle>

                        <S.List>
                            {items.map((question, questionIndex) => (
                                <S.Item key={questionIndex}>
                                    <S.ItemHeader
                                        type="button"
                                        aria-expanded={openQuestionIndex === questionIndex}
                                        onClick={() =>
                                            setOpenQuestionIndex((currentIndex) =>
                                                currentIndex === questionIndex ? null : questionIndex,
                                            )
                                        }
                                    >
                                        <S.Question>{question.title}</S.Question>
                                        <S.Sign>{openQuestionIndex === questionIndex ? '−' : '+'}</S.Sign>
                                    </S.ItemHeader>
                                    <S.AnswerWrapper $open={openQuestionIndex === questionIndex}>
                                        <div className="inner">
                                            <S.Answer>{question.raw_text}</S.Answer>
                                        </div>
                                    </S.AnswerWrapper>
                                </S.Item>
                            ))}
                        </S.List>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}
        </>
    );
};
